import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ApAuthenticationService } from '@ap/browser-authenticator';
import { ApContentService } from '@ap/content';
import { ApAuthenticatieFidoResource, FidoAuthenticationRequest, FidoAuthenticationResponse } from '@ap/interface-apic-authenticatie-fido';
import { SessiebeheerResource, Session, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { DeviceBoundToken, DevicePatch, DeviceStatus, TokenTypeId, TokenbeheerResource } from '@ap/interface-apic-tokenbeheer';

import { MijnLoginNavigation } from '../mijn-login.navigation';
import { MijnLoginService } from '../mijn-login.service';

import content from './browsercode-herstel.wrapper.content.json';

/**
 * @ngdoc component
 * @name BrowsercodeHerstelWrapperService
 * @module BrowsercodeHerstelWapperModule
 */
@Injectable({ providedIn: 'root' })
export class BrowsercodeHerstelWrapperService {
    public constructor(
        private readonly apAuthenticatieFidoResource: ApAuthenticatieFidoResource,
        private readonly apAuthenticationService: ApAuthenticationService,
        private readonly contentService: ApContentService,
        private readonly mijnLoginNavigation: MijnLoginNavigation,
        private readonly mijnLoginService: MijnLoginService,
        private readonly sessiebeheerResource: SessiebeheerResource,
        private readonly tokenbeheerResource: TokenbeheerResource
    ) {
        contentService.addContentItems(content);
    }

    public removeBrowserRegistration(): Observable<void> {
        /* eslint-disable-next-line @typescript-eslint/naming-convention */
        let LSMTokenAvailable: boolean;
        return this.doAnonymousLogin(this.mijnLoginService.getKeyChainId()).pipe(
            tap((tokenAvailable) => (LSMTokenAvailable = tokenAvailable)),
            switchMap(() => this.apAuthenticationService.getBrowserBindingKey(this.mijnLoginService.getKeyChainId())),
            switchMap((browserKeyId: string) => (LSMTokenAvailable ? this.removeBrowserFromLSM(browserKeyId) : of(null))),
            switchMap(() => this.apAuthenticationService.deleteToken(this.mijnLoginService.getKeyChainId())),
            switchMap(() =>
                LSMTokenAvailable ? this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED }) : of(null)
            )
        );
    }

    public removeBrowserFromLSM(browserKeyId: string): Observable<HttpResponse<void>> {
        const expiredPatch: DevicePatch = { status: DeviceStatus.EXPIRED };
        return this.tokenbeheerResource.getTokens({ keyIdParam: browserKeyId }).pipe(
            map((tokenPage) => {
                const containsBrowserToken =
                    tokenPage.tokens && tokenPage.tokens.length > 0 && tokenPage.tokens[0].type_id === TokenTypeId.BROWSER_DEVICE;
                if (containsBrowserToken) {
                    return (tokenPage.tokens[0] as DeviceBoundToken).device_id;
                }
                throw new Error('No browser token found');
            }),
            switchMap((deviceId) => this.tokenbeheerResource.patchDevice(deviceId, expiredPatch)),
            catchError(() => {
                this.mijnLoginNavigation.toError();
                return of(null);
            })
        );
    }

    public doAnonymousLogin(keychainId: string): Observable<boolean> {
        const requestSession: Session = {
            authentication_level: 10,
            started_from_device_fingerprint: this.apAuthenticationService.getFingerprint()
        };

        let taskId: string;
        let lsmTokenAvailable = true;
        return this.getTaskId(requestSession).pipe(
            tap((sessionTaskId: string) => (taskId = sessionTaskId)),
            switchMap(() => this.apAuthenticatieFidoResource.getOneAuthenticationRequest(taskId)),
            switchMap((request: FidoAuthenticationRequest) =>
                this.apAuthenticationService.generateAuthenticationResponse(keychainId, request)
            ),
            switchMap((authenticationResponse: FidoAuthenticationResponse) =>
                this.apAuthenticatieFidoResource.putOneAuthenticationResponse(taskId, authenticationResponse)
            ),
            switchMap(() =>
                this.sessiebeheerResource.postSession(requestSession, {
                    authenticationTaskIdHeaderParam: taskId
                })
            ),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 400) {
                    lsmTokenAvailable = false;
                } else {
                    this.mijnLoginNavigation.toError();
                }
                return of(null);
            }),
            map(() => lsmTokenAvailable)
        );
    }

    private getTaskId(requestSession: Session): Observable<string | void> {
        return this.sessiebeheerResource
            .postSession(requestSession)
            .pipe(catchError((error: HttpErrorResponse) => of(error.headers ? error.headers.get('X-Authentication-Task-Id') : null)));
    }
}
